import React from "react";
import { Helmet } from "react-helmet-async";
import { icons } from "../assets/icons";

export default function Contact() {
  return (
    <>
      <Helmet>
        <title>Contact CryptoBag | CryptoBag</title>
        <meta
          name="description"
          content="Find the current contact scope for CryptoBag, including site issues, content corrections, and platform-related questions."
        />
        <link rel="canonical" href="https://cryptobag.net/contact" />
      </Helmet>

      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="space-y-10">
          <header className="space-y-4 border-b border-gray-800/50 pb-8">
            <p className="text-sm font-bold tracking-[0.08em] text-indigo-400">Contact</p>
            <h1 className="text-4xl font-black tracking-tighter text-white sm:text-5xl">Contact and editorial requests.</h1>
            <p className="max-w-3xl text-base leading-7 text-gray-400 sm:text-lg">
              Use this page to report site problems, flag content issues, suggest corrections, or reach out about platform-related matters. CryptoBag is growing as an information platform, and contact should help improve both the product experience and the quality of the content on it.
            </p>
          </header>

          <div className="space-y-8">
            <section className="rounded-2xl border border-gray-800/50 bg-gray-900/40 p-6 backdrop-blur-sm sm:p-8">
              <div className="space-y-4">
                <h2 className="text-2xl font-black tracking-tight text-white">Primary Contact</h2>
                <p className="text-sm leading-7 text-gray-400 sm:text-base">
                  Email is the main contact path for CryptoBag. Use{" "}
                  <a
                    href="mailto:contact@cryptobag.net"
                    className="font-semibold text-indigo-400 hover:text-indigo-300 transition-colors"
                  >
                    contact@cryptobag.net
                  </a>{" "}
                  for site problems, factual corrections, content-related questions, or platform and business inquiries tied directly to the site.
                </p>
                <p className="text-sm leading-7 text-gray-400 sm:text-base">
                  For public updates and broader social presence, CryptoBag can also be followed on{" "}
                  <a
                    href="https://x.com/CryptoBagNet"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="CryptoBag on X"
                    className="inline-flex items-center text-indigo-400 hover:text-indigo-300 transition-colors"
                  >
                    {icons.xSocial({ className: "h-4 w-4" })}
                  </a>
                  .
                </p>
              </div>
            </section>

            <section className="rounded-2xl border border-gray-800/50 bg-gray-900/40 p-6 backdrop-blur-sm sm:p-8">
              <div className="space-y-4">
                <h2 className="text-2xl font-black tracking-tight text-white">Best Reasons To Reach Out</h2>
                <p className="text-sm leading-7 text-gray-400 sm:text-base">
                  The most useful outreach is the kind that helps improve the platform. That includes reporting technical issues, flagging incorrect or outdated information, giving feedback on published content, or raising questions about the structure and direction of the site.
                </p>
                <p className="text-sm leading-7 text-gray-400 sm:text-base">
                  If you are reporting a problem or suggesting a correction, include enough detail to make the issue reviewable, such as the page URL, the asset or article involved, what happened, and what you believe should be corrected.
                </p>
              </div>
            </section>

            <section className="rounded-2xl border border-gray-800/50 bg-gray-900/40 p-6 backdrop-blur-sm sm:p-8">
              <div className="space-y-4">
                <h2 className="text-2xl font-black tracking-tight text-white">Scope</h2>
                <p className="text-sm leading-7 text-gray-400 sm:text-base">
                  The contact path is meant to support the site itself, not serve as a general crypto help desk. CryptoBag does not offer individualized financial, legal, or tax advice through its contact channels.
                </p>
                <p className="text-sm leading-7 text-gray-400 sm:text-base">
                  Contact is best used for platform, content, and site-related matters rather than open-ended personal market consultation.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
